/**
 * Auto bug reporting: sends frontend errors to Azure DevOps via backend dedup service.
 */

import { config } from '../../config'
import { createServiceFetch } from '../../utils/validateFetchUrl'

const autoBugFetch = createServiceFetch(config.api.baseUrl)
import { ApiError } from '../../services/api/core'
import { isAutoMode } from './captureConfig'
import { captureBufferSnapshot } from './captureBuffer'
import { stripEmoji } from './bugTitleValidation'
import { deriveContext } from './deriveContext'
import { toInt32 } from '../../utils/int32Hash'
import { isBenignFrontendError } from '../../utils/benignFrontendErrors'

const CLIENT_THROTTLE_MS = 60_000
const lastSentByFingerprint = new Map<string, number>()

function topStackFrame(stack?: string): string {
  if (!stack) return ''
  const lines = stack.split('\n').map((l) => l.trim()).filter(Boolean)
  return lines[1] || lines[0] || ''
}

export function buildErrorFingerprint(error: Error, component?: string): string {
  const path = typeof window !== 'undefined' ? window.location.pathname : ''
  const frame = topStackFrame(error.stack)
  const raw = `${error.name}|${error.message}|${frame}|${path}|${component || ''}`
  let hash = 0
  for (let i = 0; i < raw.length; i++) {
    hash = toInt32((hash << 5) - hash + raw.charCodeAt(i))
  }
  return `fe_${Math.abs(hash).toString(36)}`
}

function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  try {
    const token = localStorage.getItem(config.auth.tokenKey)
    if (token) headers.Authorization = `Bearer ${token}`
    const tenantId = localStorage.getItem('tagp-tenant-id')
    if (tenantId) headers['X-Tenant-ID'] = tenantId
  } catch {
    /* ignore */
  }
  return headers
}

export async function maybeReportAutoBug(error: Error | unknown, component?: string): Promise<void> {
  if (!isAutoMode()) return

  const err = error instanceof Error ? error : new Error(String(error))
  if (!err.message?.trim() || isBenignFrontendError(err.message)) return

  const token = (() => {
    try {
      return localStorage.getItem(config.auth.tokenKey)
    } catch {
      return null
    }
  })()
  if (!token) return

  const fingerprint = buildErrorFingerprint(err, component)
  const now = Date.now()
  const last = lastSentByFingerprint.get(fingerprint) ?? 0
  if (now - last < CLIENT_THROTTLE_MS) return
  lastSentByFingerprint.set(fingerprint, now)

  const snapshot = captureBufferSnapshot()
  const title = stripEmoji(`[AQUA Auto] ${err.name}: ${err.message}`).slice(0, 255)
  const pageUrl = typeof window !== 'undefined' ? window.location.href : ''
  const { featureGroup, workflowId } = deriveContext(pageUrl)

  const body = {
    fingerprint,
    title,
    source: 'frontend',
    component: component || undefined,
    page_url: pageUrl,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    feature_group: featureGroup,
    workflow_id: workflowId,
    error: {
      name: err.name,
      message: err.message,
      stack: err.stack?.slice(0, 8000),
    },
    snapshot,
  }

  const url = `${config.api.baseUrl.replace(/\/+$/, '')}/v1/bug-reports/auto`

  try {
    const response = await autoBugFetch(url, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
      keepalive: true,
    })
    if (!response.ok && response.status !== 429) {
      // eslint-disable-next-line no-console
      console.debug('Auto bug report failed', response.status)
    }
  } catch {
    /* fire-and-forget */
  }
}

export function maybeReportAutoBugFromApiError(endpoint: string, error: unknown): void {
  if (!isAutoMode()) return
  if (!(error instanceof ApiError)) return
  if (error.status > 0 && error.status < 500) return
  void maybeReportAutoBug(error, `api:${endpoint}`)
}
